'use client';

import { NavIcon } from '@/components/ui/icon';
import { useTheme } from '@/hooks/useTheme';

interface ThemeToggleProps {
  collapsed?: boolean;
  className?: string;
}

type ThemeOption = 'light' | 'dark' | 'system';

export default function ThemeToggle({ collapsed = false, className = '' }: ThemeToggleProps) {
  const { theme, effectiveTheme, setTheme, mounted } = useTheme();
  const isDark = effectiveTheme === 'dark';

  const getThemeLabel = () => {
    if (theme === 'system') return 'System';
    return theme === 'dark' ? 'Dark' : 'Light';
  };

  const getThemeIcon = (themeMode: ThemeOption) => {
    switch (themeMode) {
      case 'light':
        return 'sun';
      case 'dark':
        return 'moon';
      case 'system':
        return 'monitor'; // or 'laptop' - adjust based on your icon set
      default:
        return 'sun';
    }
  };

  const cycleTheme = () => {
    const themes: ThemeOption[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme as ThemeOption);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setTheme(nextTheme);
  };

  if (!mounted) {
    return (
      <div
        className={`flex items-center ${collapsed ? 'justify-center' : 'justify-start'} ${className}`}
      >
        <div className="bg-border h-5 w-5 animate-pulse rounded" />
        {!collapsed && <div className="bg-border ml-2 h-4 w-16 animate-pulse rounded" />}
      </div>
    );
  }

  if (collapsed) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={cycleTheme}
          className={`group hover:bg-foreground/[0.06] hover:border-accent/30 text-foreground/80 flex h-10 w-12 items-center justify-center rounded-md border border-transparent transition-colors ${className}`}
          title={`Theme: ${getThemeLabel()} - Click to cycle`}
          aria-label={`Current theme: ${getThemeLabel()}, click to cycle`}
        >
          <NavIcon name={getThemeIcon(theme as ThemeOption)} className="h-5 w-5" />
        </button>
      </div>
    );
  }

  // Enhanced toggle with system mode indicator
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <button
        onClick={cycleTheme}
        className={`relative inline-flex h-[32px] w-[64px] items-center rounded-full transition-all duration-300 focus:ring-2 focus:ring-blue-500/20 focus:outline-none ${
          theme === 'light'
            ? 'bg-blue-500'
            : theme === 'dark'
              ? 'bg-gray-900 dark:bg-black'
              : 'bg-gradient-to-r from-blue-500 to-gray-900'
        }`}
        role="switch"
        aria-checked={isDark}
        aria-label="Cycle through theme options"
      >
        {/* System mode indicator */}
        {theme === 'system' && (
          <div className="absolute inset-0 rounded-full border-2 border-dashed border-white/30" />
        )}

        {/* Track background with icons */}
        <div className="absolute inset-0 flex items-center justify-between overflow-hidden rounded-full px-2">
          <div className="flex h-6 w-6 items-center justify-center">
            <NavIcon name="sun" className="h-4 w-4 text-white" />
          </div>
          <div className="flex h-6 w-6 items-center justify-center">
            <NavIcon name="moon" className="h-4 w-4 text-white" />
          </div>
        </div>

        {/* Sliding circle */}
        <span
          className={`absolute flex h-7 w-7 transform items-center justify-center rounded-full bg-white shadow-lg transition-all duration-300 ${
            theme === 'light'
              ? 'translate-x-[3px]'
              : theme === 'dark'
                ? 'translate-x-[34px]'
                : 'translate-x-[18.5px]' // Center for system
          }`}
        >
          <NavIcon
            name={theme === 'system' ? 'sun' : getThemeIcon(theme as ThemeOption)}
            className="h-3 w-3 text-gray-600"
          />
        </span>
      </button>

      <span className="text-muted text-sm font-medium transition-all duration-300">
        {theme === 'dark' ? 'Dark' : theme === 'light' ? 'Light' : 'Auto'}
      </span>
    </div>
  );
}
